import { InsuranceCarrier } from '@shared/insuranceCarrierDefs';
import { FundingPolicyType } from './appModel';

// Currently no feature flags for carriers. All carriers are enabled.
const CARRIER_FEATURE_FLAG_MAP: Partial<Record<InsuranceCarrier, FeatureFlagKeys | null>> = {
  [InsuranceCarrier.CareScout]: 'quoteApplyCareScout',
  [InsuranceCarrier.LincolnFinancial]: null,
  [InsuranceCarrier.SecurianFinancial]: null,
  [InsuranceCarrier.EquiTrust]: 'quoteApplyEquiTrust',
  [InsuranceCarrier.NewYorkLife]: 'quoteApplyNewYorkLife',
  [InsuranceCarrier.ManhattanLife]: 'quoteApplyManhattanLife',
};

/**
 * Opt-in flags at funding-policy-type granularity, parallel to
 * CARRIER_FEATURE_FLAG_MAP but one level down. A policy type listed here is
 * gated on the quote/illustration endpoints unless the advisor has the mapped
 * flag enabled (default OFF / opt-in) — it does NOT hide the type from the
 * manual add-policy form, which stays open so advisors can record a policy a
 * client already owns. Used to gate a single product on production without
 * affecting sibling products from the same carrier (e.g. gate SecureCare IV
 * while leaving SecureCare III untouched).
 */
const POLICY_TYPE_FEATURE_FLAG_MAP: Partial<Record<FundingPolicyType, FeatureFlagKeys>> = {
  [FundingPolicyType.manhattanLifeOmniFlexStc]: 'quoteApplyManhattanLife',
};

export function getCarrierFeatureFlag(carrierId: InsuranceCarrier): FeatureFlagKeys | null {
  return CARRIER_FEATURE_FLAG_MAP[carrierId] || null;
}

/**
 * Returns whether a funding policy type is available to an advisor. Policy types
 * not listed in POLICY_TYPE_FEATURE_FLAG_MAP are always available; gated ones
 * require the advisor to have the mapped feature flag enabled.
 */
export function isPolicyTypeEnabledForAdvisor(
  policyType: FundingPolicyType,
  featureFlags?: FeatureFlags,
): boolean {
  const flagKey = POLICY_TYPE_FEATURE_FLAG_MAP[policyType];
  if (!flagKey) {
    return true;
  }
  return Boolean(featureFlags?.[flagKey]);
}

export function calculateEnabledCarrierIds(featureFlags?: FeatureFlags): number[] {
  const enabledCarriers: number[] = [];

  for (const [carrierId, featureFlagKey] of Object.entries(CARRIER_FEATURE_FLAG_MAP)) {
    const carrierIdNum = Number(carrierId);

    if (featureFlagKey === null) {
      enabledCarriers.push(carrierIdNum);
    } else if (featureFlags?.[featureFlagKey]) {
      enabledCarriers.push(carrierIdNum);
    }
  }

  return enabledCarriers;
}
